import { Activity, Gauge, Trash2 } from "lucide-mui";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  PROSPECT_ORDER_TRIGGER,
  SCHEDULE_TRIGGER,
  SIGNAL_TRIGGER,
  api,
  scheduleTriggerConfigOf,
  signalTriggerConfigOf,
  type Workflow,
  type WorkflowStepType,
} from "../../api/client.ts";
import { useWorkflowDataTypes } from "../../hooks/resources.ts";
import { keys } from "../../api/queryKeys.ts";
import FilterBar from "../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { type FilterState, useFilters } from "../../components/filters/useFilters.ts";
import RelativeTime from "../../components/RelativeTime.tsx";
import RowActions from "../../components/RowActions.tsx";
import EmptyTableRow from "../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../components/table/ScrollTable.tsx";
import TableCard from "../../components/table/TableCard.tsx";
import { userHasGlobalScope } from "../../auth/scopes.ts";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import { useUrlState } from "../../hooks/useUrlState.ts";
import { formatDate } from "../../utils/format.ts";
import { PALETTE_KINDS, TRIGGER_META, isTriggerType } from "./editor/types.ts";
import { scheduleLabel } from "./schedule.ts";

// Step chips in palette order, with a count when a type repeats.
function stepSummary(
  workflow: Workflow,
  stepTypes: WorkflowStepType[],
): { slug: string; label: string; count: number; enabled: boolean }[] {
  const counts = new Map<string, { count: number; enabled: boolean }>();
  for (const s of workflow.steps) {
    const cur = counts.get(s.step_type) ?? { count: 0, enabled: false };
    counts.set(s.step_type, { count: cur.count + 1, enabled: cur.enabled || s.enabled });
  }
  const order = (slug: string) => {
    const i = (PALETTE_KINDS as string[]).indexOf(slug);
    return i === -1 ? PALETTE_KINDS.length : i;
  };
  return [...counts.entries()]
    .sort(([a], [b]) => order(a) - order(b))
    .map(([slug, { count, enabled }]) => ({
      slug,
      label: stepTypes.find((t) => t.slug === slug)?.label ?? slug,
      count,
      enabled,
    }));
}

function WorkflowsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectedTenantId, tenantPath } = useTenant();
  const { user } = useAuth();
  const canWrite = userHasGlobalScope(user, "signal_rules:write");
  const canReadSignals = userHasGlobalScope(user, "signals:read");
  // `?trigger=` keeps the filter in the URL so other pages can link straight
  // to their kind of workflow.
  const url = useUrlState();
  const rawTrigger = url.searchParams.get("trigger");
  const triggerFilter = isTriggerType(rawTrigger) ? rawTrigger : "";
  const dataTypeFilter = url.searchParams.get("data_type") ?? "";
  // Prospect orders and schedules watch no data stream.
  const showDataType = triggerFilter !== PROSPECT_ORDER_TRIGGER && triggerFilter !== SCHEDULE_TRIGGER;

  const { data: triggerTypes = [] } = useQuery({
    queryKey: keys.workflowTriggerType.list(),
    queryFn: () => api.getWorkflowTriggerTypes(),
  });

  const { data: dataTypes = [] } = useWorkflowDataTypes();

  const { data: stepTypes = [] } = useQuery({
    queryKey: keys.workflowStepType.list(),
    queryFn: () => api.getWorkflowStepTypes(),
  });

  const effectiveDataType = showDataType ? dataTypeFilter : "";
  const { data: workflows, isLoading } = useQuery({
    queryKey: keys.workflow.list(selectedTenantId, triggerFilter || null, effectiveDataType || null),
    queryFn: () =>
      api.getWorkflows({
        tenant_id: selectedTenantId ?? undefined,
        trigger_type: triggerFilter || undefined,
        data_type: effectiveDataType || undefined,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.workflow.all });

  const activeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.updateWorkflow(id, { active }),
    onSuccess: (wf) => {
      queryClient.setQueryData(keys.workflow.detail(wf.id), wf);
      invalidate();
      toast.success(wf.active ? "Workflow activated" : "Workflow paused");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteWorkflow(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: keys.signal.all });
      toast.success("Workflow deleted");
    },
  });

  const dataTypeLabel = (slug: string) =>
    dataTypes.find((d) => d.slug === slug)?.label ?? slug;
  // One vocabulary for every workflow: when it starts. A signal is what a
  // matching record becomes, not a kind of workflow.
  const triggerLabel = (slug: string) =>
    isTriggerType(slug)
      ? TRIGGER_META[slug].label
      : (triggerTypes.find((t) => t.slug === slug)?.label ?? slug);

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "trigger",
        label: "Starts when",
        param: "trigger",
        multiple: false,
        pinned: true,
        options: triggerTypes.map((t) => ({
          value: t.slug,
          label: isTriggerType(t.slug) ? TRIGGER_META[t.slug].label : t.label,
        })),
      },
      ...(showDataType
        ? [
            {
              kind: "options",
              key: "data_type",
              label: "Data type",
              param: "data_type",
              multiple: false,
              pinned: true,
              options: dataTypes.map((dt) => ({ value: dt.slug, label: dt.label })),
            } satisfies FilterSpec,
          ]
        : []),
    ],
    [triggerTypes, dataTypes, showDataType],
  );
  // Picking "prospect order placed" drops the data type it can't have.
  const { searchParams, changeFilter } = url;
  const filterState = useMemo<FilterState>(
    () => ({
      searchParams,
      changeFilter: (updates, replace) =>
        changeFilter(
          updates.trigger === PROSPECT_ORDER_TRIGGER || updates.trigger === SCHEDULE_TRIGGER
            ? { ...updates, data_type: null }
            : updates,
          replace,
        ),
    }),
    [searchParams, changeFilter],
  );
  const filters = useFilters(specs, { state: filterState });
  const filtered = !!(triggerFilter || effectiveDataType);

  return (
    <>
      <TableCard
        loading={isLoading}
        toolbar={
          <FilterBar
            filters={filters}
            count={workflows ? `${workflows.length} ${workflows.length === 1 ? "workflow" : "workflows"}` : undefined}
          />
        }
      >
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 180 }}>Name</TableCell>
              <TableCell>Starts when</TableCell>
              <TableCell>Watches</TableCell>
              <TableCell>Steps</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell sx={{ width: 80 }}>Active</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {workflows?.length === 0 && (
              <EmptyTableRow colSpan={7} align={filtered ? "center" : "left"}>
                {filtered
                  ? "No workflows match your filters"
                  : "No workflows yet. Start one when a record matches (a job ad, a news story, a " +
                    "Notion page) to surface buying signals or create prospect orders, when a " +
                    "prospect order is placed to have the prospecting agent work it, or on a " +
                    "schedule to have an agent report every morning."}
              </EmptyTableRow>
            )}
            {(workflows ?? []).map((wf) => {
              const isSignal = wf.trigger_type === SIGNAL_TRIGGER;
              const isSchedule = wf.trigger_type === SCHEDULE_TRIGGER;
              const trigger = isSignal ? signalTriggerConfigOf(wf) : null;
              const steps = stepSummary(wf, stepTypes);
              return (
                <TableRow
                  key={wf.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(tenantPath(`/workflows/${wf.id}`))}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {wf.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {wf.source === "onboarding"
                        ? "suggested at onboarding"
                        : wf.source === "generated"
                          ? "auto-generated"
                          : "manual"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={triggerLabel(wf.trigger_type)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {trigger ? (
                      <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
                        <Chip label={dataTypeLabel(trigger.data_type)} size="small" />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {trigger.prompt}
                        </Typography>
                      </Stack>
                    ) : isSchedule ? (
                      <Stack spacing={0.25}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          {scheduleLabel(scheduleTriggerConfigOf(wf))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {wf.next_fire_at
                            ? `Next run ${formatDate(wf.next_fire_at, "datetime")}`
                            : "Paused"}
                          {wf.last_fired_at && <> · last <RelativeTime value={wf.last_fired_at} /></>}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Every new prospect order for this tenant
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {steps.length === 0 ? (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        No steps
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                        {steps.map((s) => (
                          <Chip
                            key={s.slug}
                            size="small"
                            variant="outlined"
                            label={s.count > 1 ? `${s.label} ×${s.count}` : s.label}
                            sx={{ opacity: s.enabled ? 1 : 0.6 }}
                          />
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <RelativeTime value={wf.updated_at} />
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={wf.active}
                      onChange={(e) =>
                        activeMutation.mutate({ id: wf.id, active: e.target.checked })
                      }
                      disabled={!canWrite || activeMutation.isPending}
                    />
                  </TableCell>
                  <TableCell padding="checkbox" align="right">
                    <RowActions
                      label={wf.name}
                      actions={[
                        // A signal workflow's matches and how it performs, on the section's other tabs.
                        {
                          label: "Signals",
                          icon: <Activity fontSize="small" />,
                          hidden: !isSignal || !canReadSignals,
                          onClick: () => navigate(tenantPath(`/workflows/signals?workflow=${wf.id}`)),
                        },
                        {
                          label: "Performance",
                          icon: <Gauge fontSize="small" />,
                          hidden: !isSignal || !canReadSignals,
                          onClick: () => navigate(tenantPath(`/workflows/signals/performance?workflow=${wf.id}`)),
                        },
                        {
                          label: "Delete",
                          icon: <Trash2 fontSize="small" />,
                          destructive: true,
                          hidden: !canWrite,
                          onClick: () => deleteMutation.mutateAsync(wf.id),
                          confirm: {
                            title: "Delete workflow",
                            body: (
                              <>
                                Delete <strong>{wf.name}</strong>?{" "}
                                {isSignal
                                  ? "It stops evaluating new records; the signals and research briefs it already produced stay listed under its name."
                                  : isSchedule
                                    ? "It stops running on its schedule; runs already started finish."
                                    : "New prospect orders stop running it; what it already did on earlier orders stays on their pages."}
                              </>
                            ),
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ScrollTable>
      </TableCard>
    </>
  );
}

export default WorkflowsPage;
